import { kObject, kObjectArray } from "../ReUsableTS/kBrowserObjects";
import { kHTMLEditableColType, kStatusCellRenderer } from "./kEditableTableTypes";
import type { IkColumnSpec, kCellStatus, kRowFormFieldControl } from "./kEditableTableTypes";
import ks from "../ReUsableTS/kBrowserShared";
import { kModalDialog } from "../ReUsableTS/kModalDialog";
import LNG from "../ReUsableTS/kLNG";
import type { kMultiSelectOption } from "../ReUsableTS/kHTMLMultiSelect";
import type { kEditableTableCell } from "./kEditableTableCell";

// kEditableColumn / kEditableColumns / kNewChoiceCollector, moved out of kEditableTable.ts (size split, 2026-10-20); re-exported from kEditableTable.ts.

export class kEditableColumn extends kObject {
    public Width: number = 100;
    public Type: kHTMLEditableColType = kHTMLEditableColType.Text;
    public Options: Array<string> = new Array<string>();
    // Optional per-option hover-tooltip text, keyed by the option string.
    // When a Select cell holds a value that has an entry here, the cell shows
    // it as a native `title` tooltip (read-mode) and the edit dropdown's
    // matching <option> is titled too. Empty (the default) leaves cells
    // untouched, so existing callers are unaffected. The map is supplied by
    // the consumer — kEditableTable stays project-agnostic and never resolves
    // the text itself.
    public OptionTooltips: { [k: string]: string } = {};
    // Optional hover-tooltip on the column header <th> (e.g. the column's
    // authoring guidance: "Comma-separated list of Annex A IDs…"). Empty (the
    // default) leaves the header untouched.
    public HeaderTooltip: string = "";
    // Optional accessible name for the checkbox in each cell of a Checkbox column (aria-label on the
    // role=checkbox element), e.g. "Select" for a pick column whose header is empty. Empty (the
    // default) leaves the markup as it was.
    public CheckboxLabel: string = "";
    public MultiSelectOptions: Array<kMultiSelectOption> =[];
    public MultiSelectNoneSelectedOptionText:string ="";
    public KObjectPropertyName: string = "Title";
    // Optional tab/group name for the "Edit in form" dialog. When ANY column
    // on the grid carries a non-empty Tab, the row form groups its fields into
    // a tab strip keyed off this string (natural-sorted; columns left empty
    // collect under a leading "General" tab). Empty (the default) on every
    // column renders one flat form. Has no effect on the inline grid itself.
    public Tab: string = "";
    public Locked: boolean = false;
    // Optional extra CSS class applied to this column's header cell AND every one
    // of its content cells (additive — empty, the default, changes nothing). Lets a
    // consumer stylesheet single out ONE column (e.g. tag a read-only reference
    // column) on top of the shared Locked styling, without the grid learning the
    // domain. Mirrors Locked/Frozen/Resizable as a per-column flag. NB a class-based
    // background from a consumer stylesheet CANNOT override this component's own
    // `.kEditableTableCell{ background:white !important }` — use TintColor below for a
    // background, which is applied inline-important and always wins.
    public CssClass: string = "";
    // Optional background colour for this column's header + every content cell,
    // applied as an INLINE `!important` style so it beats the component's own
    // `.kEditableTableCell{ background:white !important }` (and the header's
    // `#f2f2f2 !important`) with no consumer stylesheet needed. Empty (the default)
    // leaves the standard white/grey. This is the supported way to tint one column
    // (e.g. mark a read-only reference column) — a plain CSS rule is silently
    // overridden by the base !important, which is easy to miss (it renders white).
    public TintColor: string = "";
    public DefaultValue: any = null;
    public Icon:HTMLImageElement|null|undefined = null;
    public Resizable: boolean = true;
    // When false, the column is excluded from all filtering surfaces (header
    // funnel, context-menu entry, global search highlighting) and from the
    // global quick-filter row text. Defaults to true for data columns; the
    // grid forces it false for Button columns (pure action cells) when it
    // builds the per-column filter affordances. Mirrors Locked/Frozen/Resizable.
    public Filterable: boolean = true;
    public Frozen: boolean = false;
    // The faint magnitude bar behind a Number/Currency cell. Set false to drop it for
    // this column (kEditableTable.ShowDataBars switches it off for the whole grid).
    public ShowDataBar: boolean = true;
    public _FrozenLeft: number = -1;
    public CustomRenderer: ((value: any, cell: kEditableTableCell) => void) | null = null;
    // First-class per-cell HEAT/STATUS colouring: wire a value->kCellStatus provider
    // through the shared kStatusCellRenderer (into CustomRenderer). Unlike TintColor
    // (whole column) this colours each cell BY ITS VALUE, applied inline-important, and
    // the consumer returns pure data (no DOM). Null clears it. The declarative successor
    // to hand-rolling a CustomRenderer for coverage heatmaps / RAG columns.
    public SetStatusProvider = (Provider: ((value: any) => kCellStatus | null) | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Provider === null) { this.CustomRenderer = null; return; }
            this.CustomRenderer = kStatusCellRenderer(Provider);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumn.SetStatusProvider");
        }
    };
    // Applies this column's styling to one of its cells or its header, in this order: the
    // LockedClass when Locked, the consumer CssClass, and TintColor as an inline-important
    // background (beats the base white / grey !important). Shared by the content cells
    // (kEditableTable._CreateContentCell) and the header (kEditableTableSortDrag._BuildColumnHeader).
    public ApplyStyleTo = (El: HTMLElement, LockedClass: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Locked === true) { El.classList.add(LockedClass); }
            if (this.CssClass !== "") { El.classList.add(this.CssClass); }
            if (this.TintColor !== "") { El.style.setProperty("background-color", this.TintColor, "important"); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumn.ApplyStyleTo");
        }
    };
    // Optional per-column editor override. When set, clicking a cell in this
    // column invokes it INSTEAD of the built-in editor for the column's Type
    // — the consumer fully owns the editing UX (popup, picker, dialog) and
    // commits via Cell.SetNewValue(...). Pairs with CustomRenderer (which owns
    // the read-mode painting) to make a fully custom cell without the base
    // grid learning the domain (people pickers, document links, …). Null (the
    // default) keeps the built-in editors; existing callers are unaffected.
    public CustomEditor: ((cell: kEditableTableCell) => void) | null = null;
    // Optional per-column inline form control for the "Edit in form" dialog.
    // When set, the row form embeds this control instead of falling back to a
    // delegating "Edit…" button — so a custom cell (people picker, document
    // link) is edited in place in the form, exactly like the native fields.
    // Returns null to decline (the form then delegates). Pairs with
    // CustomEditor (cell-level) / CustomRenderer (read mode); supplied by the
    // same shared module so the base grid never learns the domain.
    public FormFieldFactory: ((cell: kEditableTableCell) => kRowFormFieldControl | null) | null = null;
    public ValidationCB: ((value: any) => string | null) | null = null;
    // Optional callback invoked when the user picks the "add new item" entry a
    // Select / SelectMulti column always offers at the foot of its dropdown. The
    // consumer OWNS the "capture a new choice" UX (its own dialog, a REST write to
    // a SharePoint choice field, …) and reports the chosen name back by invoking
    // the supplied Commit callback with the new value ("" / whitespace cancels).
    // When null (the default) the grid shows a built-in single-input dialog asking
    // for the name. The add-new affordance is enabled on EVERY Select / SelectMulti
    // column — there is no per-column opt-in; a column that must NOT grow its choices
    // sets AllowAddNewItem = false. Added 2026-07-02.
    public OnNewItem: ((column: kEditableColumn, commit: (newItem: string) => void) => void) | null = null;
    // Opt-OUT switch for the always-on add-new affordance above. True (the default)
    // means Select / SelectMulti cells offer "add new item"; set false to lock the
    // column to its configured Options (e.g. a fixed status set).
    public AllowAddNewItem: boolean = true;
    // RichHtml columns only: size of the popup editor and an optional
    // jqxEditor tool-string override (maps to kJQXEditor.CustomTools).
    public RichHtmlEditorWidth: number = 520;
    public RichHtmlEditorHeight: number = 260;
    public RichHtmlTools: string = "";
}

export class kEditableColumns extends kObjectArray{
    public Items: kEditableColumn[] = [];

    /**
     * Build a column set from a table of specs.
     *
     * WHY THIS EXISTS. A grid over a wide list is thirty near-identical eight-line blocks of
     * `new kEditableColumn(); C.Title = ...; C.KObjectPropertyName = ...; C.Locked = true; ...`.
     * That is not configuration, it is transcription, and it goes wrong in the quiet way: a
     * `KObjectPropertyName` that does not match any property on the bound object renders an empty
     * column with no error, and reads as "there is no data" rather than "this column is misspelt".
     * Declaring the set as data makes the whole grid reviewable at a glance and lets a product
     * generate it from a schema it already has.
     *
     * `Locked` as an argument rather than a per-spec flag is deliberate: a read-only grid is the
     * common case for a register, and repeating the flag thirty times invites the one row that
     * forgets it — which is a silently editable column in a view that is supposed to be read-only.
     *
     * Additive: no existing caller is affected, because nothing called this before.
     */
    public static FromSchema = (Specs: IkColumnSpec[], Locked: boolean = true): kEditableColumn[] => {
        if (ks.KErrorMode === true) { return []; }
        try {
            const Built: kEditableColumn[] = [];
            for (const Spec of Specs) {
                Built.push(kEditableColumns.ColumnFromSpec(Spec, Locked));
            }
            return Built;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns.FromSchema");
            return [];
        }
    };

    /** One spec to one column. Split out so the loop above stays readable and this stays testable. */
    public static ColumnFromSpec = (Spec: IkColumnSpec, Locked: boolean): kEditableColumn => {
        if (ks.KErrorMode === true) { return new kEditableColumn(); }
        try {
            const Column: kEditableColumn = new kEditableColumn();
            Column.KObjectPropertyName = Spec.InternalName;
            Column.Title = kEditableColumns._Text(Spec.Header, Spec.InternalName);
            Column.HeaderTooltip = kEditableColumns._Text(Spec.HeaderTooltip, "");
            Column.Type = kEditableColumns._Type(Spec.Type);
            Column.Width = kEditableColumns._Number(Spec.Width, 120);
            Column.Locked = kEditableColumns._Flag(Spec.Locked, Locked);
            Column.Frozen = kEditableColumns._Flag(Spec.Frozen, false);
            Column.Filterable = kEditableColumns._Flag(Spec.Filterable, true);
            Column.Tab = kEditableColumns._Text(Spec.Tab, "");
            Column.CssClass = kEditableColumns._Text(Spec.CssClass, "");
            if (Spec.Options !== undefined && Spec.Options !== null) { Column.Options = Spec.Options; }
            if (Spec.CustomRenderer !== undefined && Spec.CustomRenderer !== null) {
                Column.CustomRenderer = Spec.CustomRenderer;
            }
            return Column;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns.ColumnFromSpec");
            return new kEditableColumn();
        }
    };

    /** Defaulting helpers. They take the full guard/try-catch pattern rather than claiming the
     *  pure-helper carve-out: each already receives the value it should fall back to, so returning
     *  that fallback in error mode is both correct and free — there is no constant of their own for
     *  a guard to recurse on, which is the only thing the carve-out exists to avoid. */
    private static _Text = (Value: string | undefined, Fallback: string): string => {
        if (ks.KErrorMode === true) { return Fallback; }
        try {
            if (Value === undefined || Value === null || Value === "") { return Fallback; }
            return Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns._Text");
            return Fallback;
        }
    };

    private static _Number = (Value: number | undefined, Fallback: number): number => {
        if (ks.KErrorMode === true) { return Fallback; }
        try {
            if (typeof Value !== "number") { return Fallback; }
            return Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns._Number");
            return Fallback;
        }
    };

    private static _Flag = (Value: boolean | undefined, Fallback: boolean): boolean => {
        if (ks.KErrorMode === true) { return Fallback; }
        try {
            if (typeof Value !== "boolean") { return Fallback; }
            return Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns._Flag");
            return Fallback;
        }
    };

    private static _Type = (Value: kHTMLEditableColType | undefined): kHTMLEditableColType => {
        if (ks.KErrorMode === true) { return kHTMLEditableColType.Text; }
        try {
            if (typeof Value !== "number") { return kHTMLEditableColType.Text; }
            return Value;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableColumns._Type");
            return kHTMLEditableColType.Text;
        }
    };
}

// Collects the name of a NEW choice for a Select / SelectMulti column. When the
// column carries an OnNewItem callback the consumer owns the capture UX (its own
// dialog / REST write) and reports back through Commit; otherwise a built-in
// single-input kModalDialog asks for the name. Either way the trimmed name (or ""
// on cancel) is handed to the OnDone callback supplied by the cell. One instance
// per collection run — it holds the run's context so no closures / .bind() are
// needed (constitution: named handlers, each object owns its state).
export class kNewChoiceCollector {
    private Column: kEditableColumn | null = null;
    private OnDone: ((newItem: string) => void) | null = null;
    private Dialog: kModalDialog | null = null;
    private Input: HTMLInputElement | null = null;

    public Collect = (Column: kEditableColumn, OnDone: (newItem: string) => void): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Column = Column;
            this.OnDone = OnDone;
            if (Column.OnNewItem !== null) {
                Column.OnNewItem(Column, this._Commit);
                return;
            }
            this._ShowDialog();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector.Collect");
        }
    };

    private _ShowDialog = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.Dialog = new kModalDialog(LNG.ADDNEW, "", null, 170, 380, true);
            this.Dialog.DestroyOnClose = true;
            this.Dialog.showOKButton = true;
            this.Dialog.OKCB = this._OnDialogOK;
            // Fires on EVERY close path (OK, Cancel, Escape, ×, backdrop). _Commit
            // is idempotent (it nulls OnDone after the first call), so an OK commits
            // the typed name and this later no-ops, while a cancel reaches here first
            // and commits "" — the caller always hears back and can reset its state.
            this.Dialog.OnClosedCB = this._OnDialogClosed;

            const Label: HTMLLabelElement = document.createElement("label");
            Label.textContent = LNG.NAME;
            Label.style.display = "block";
            Label.style.marginBottom = "6px";
            Label.style.fontWeight = "600";

            this.Input = document.createElement("input");
            this.Input.type = "text";
            this.Input.className = "kNewChoiceInput";
            this.Input.style.width = "100%";
            this.Input.style.boxSizing = "border-box";
            this.Input.style.padding = "7px 10px";
            this.Input.style.borderRadius = "6px";
            this.Input.style.border = "1px solid #d2d0ce";
            this.Input.addEventListener("keydown", this._InputKeyDown);

            const Wrap: HTMLDivElement = document.createElement("div");
            Wrap.appendChild(Label);
            Wrap.appendChild(this.Input);
            this.Dialog.AddContent(Wrap);
            this.Dialog.Show();
            this.Input.focus();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector._ShowDialog");
        }
    };

    private _InputKeyDown = (Event: KeyboardEvent): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Event.key === "Enter") {
                Event.preventDefault();
                Event.stopPropagation();
                if (this.Dialog !== null) {
                    this._OnDialogOK();
                    this.Dialog.Close();
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector._InputKeyDown");
        }
    };

    private _OnDialogOK = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            let Value: string = "";
            if (this.Input !== null) { Value = ks.Nz(this.Input.value); }
            this._Commit(Value);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector._OnDialogOK");
        }
    };

    private _OnDialogClosed = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this._Commit(""); // no-op after an OK commit; a cancel reaches here first
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector._OnDialogClosed");
        }
    };

    private _Commit = (NewItem: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.OnDone === null) { return; }
            const Done: (newItem: string) => void = this.OnDone;
            this.OnDone = null; // guard against a double-commit (dialog OK + Enter)
            Done(ks.Nz(NewItem).trim());
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kNewChoiceCollector._Commit");
        }
    };
}
